import { Download, FileSpreadsheet, ShieldCheck, Upload, XCircle } from 'lucide-react';
import { useRef, useState } from 'react';

import { MigrationClientTable } from '@/components/admin/migration/MigrationClientTable';
import { MigrationIssueList } from '@/components/admin/migration/MigrationIssueList';
import { MigrationRmTable } from '@/components/admin/migration/MigrationRmTable';
import { MigrationSummary } from '@/components/admin/migration/MigrationSummary';
import { Card, ConfirmButton, useToast } from '@/components/ui';
import { Button } from '@/components/ui/button';
import { Select } from '@/components/ui/select-menu';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { useCommitMigration, useValidateMigration } from '@/hooks/useMigration';
import {
  downloadProcessedWorkbook,
  parseMigrationWorkbook,
  type ParsedMigrationWorkbook,
} from '@/lib/migrationWorkbook';
import type {
  MigrationAumUnit,
  MigrationPayload,
  MigrationReport,
} from '@/services/migration.service';
import { errorMessage } from '@/utils/apiError';

const AUM_UNIT_OPTIONS = [
  { value: 'crores', label: 'AUM in crores (as the template asks)' },
  { value: 'rupees', label: 'AUM in rupees (converted to crores)' },
];

// Upload -> dry run (nothing written) -> review every reason -> import. The
// import re-validates on the server, so a stale report can never write data the
// CRM no longer agrees with.
const AdminMigrationPage = () => {
  const { toast } = useToast();
  const inputRef = useRef<HTMLInputElement>(null);
  const [fileName, setFileName] = useState<string | null>(null);
  const [workbook, setWorkbook] = useState<ParsedMigrationWorkbook | null>(null);
  const [aumUnit, setAumUnit] = useState<MigrationAumUnit>('crores');
  const [report, setReport] = useState<MigrationReport | null>(null);
  const validate = useValidateMigration();
  const commit = useCommitMigration();
  const busy = validate.isPending || commit.isPending;

  const payloadFor = (wb: ParsedMigrationWorkbook, unit: MigrationAumUnit): MigrationPayload => ({
    aumUnit: unit,
    sheets: wb.sheets,
  });

  const runValidation = (wb: ParsedMigrationWorkbook, unit: MigrationAumUnit) => {
    setReport(null);
    validate.mutate(payloadFor(wb, unit), {
      onSuccess: setReport,
      onError: (error) => toast.error(errorMessage(error, 'Could not validate the workbook')),
    });
  };

  const onFile = async (file: File | undefined) => {
    if (!file) return;
    try {
      const parsed = await parseMigrationWorkbook(file);
      setFileName(file.name);
      setWorkbook(parsed);
      runValidation(parsed, aumUnit);
    } catch {
      toast.error('That file could not be read as an Excel workbook');
    } finally {
      if (inputRef.current) inputRef.current.value = '';
    }
  };

  const onImport = () => {
    if (!workbook) return;
    commit.mutate(payloadFor(workbook, aumUnit), {
      onSuccess: (result) => {
        setReport(result);
        toast.success(`${result.summary.clientsImport} client(s) imported`);
      },
      onError: (error) => toast.error(errorMessage(error, 'The import failed')),
    });
  };

  const onDownload = () => {
    if (!workbook || !report || !fileName) return;
    const base = fileName.replace(/\.xlsx?$/i, '');
    downloadProcessedWorkbook(workbook.buffer, report, `${base} - ${report.mode === 'commit' ? 'imported' : 'validated'}.xlsx`);
  };

  const skipped = report?.clients.filter((c) => c.outcome === 'skip') ?? [];
  const ready = report?.clients.filter((c) => c.outcome === 'import') ?? [];
  const existing = report?.clients.filter((c) => c.outcome === 'existing') ?? [];
  const canImport =
    report?.mode === 'validate' && report.fileErrors.length === 0 && report.summary.clientsImport > 0;

  return (
    <div className="space-y-4">
      <div>
        <h1 className="text-lg font-bold text-foreground">Data Migration</h1>
        <p className="text-sm text-muted-foreground">
          Upload the existing-clients workbook. Every row is checked against the sheet itself and
          the CRM first - nothing is written until you import, and only clients that pass every
          check are imported. Clients already in the CRM are never changed.
        </p>
      </div>

      <Card className="space-y-4 p-5">
        <div className="flex flex-wrap items-end gap-3">
          <div className="space-y-1.5">
            <p className="text-xs font-semibold text-muted-foreground">AUM unit used in the sheet</p>
            <Select
              value={aumUnit}
              options={AUM_UNIT_OPTIONS}
              disabled={busy}
              aria-label="AUM unit"
              className="w-72"
              onChange={(e) => {
                const unit = e.target.value as MigrationAumUnit;
                setAumUnit(unit);
                if (workbook) runValidation(workbook, unit);
              }}
            />
          </div>
          <input
            ref={inputRef}
            type="file"
            accept=".xlsx,.xls"
            className="hidden"
            onChange={(e) => {
              void onFile(e.target.files?.[0]);
            }}
          />
          <Button onClick={() => inputRef.current?.click()} disabled={busy}>
            <Upload className="mr-1.5 h-4 w-4" />
            {fileName ? 'Upload another workbook' : 'Upload workbook'}
          </Button>
          {fileName && (
            <span className="inline-flex items-center gap-1.5 text-sm text-muted-foreground">
              <FileSpreadsheet className="h-4 w-4" />
              {fileName}
            </span>
          )}
        </div>
        {busy && (
          <p className="text-sm text-muted-foreground">
            {commit.isPending
              ? 'Importing - each client is written in its own transaction, this can take a minute…'
              : 'Validating every row against the CRM…'}
          </p>
        )}
      </Card>

      {report && report.fileErrors.length > 0 && (
        <Card className="flex items-start gap-3 border-destructive/40 p-4">
          <XCircle className="mt-0.5 h-5 w-5 shrink-0 text-destructive" />
          <div>
            <p className="text-sm font-bold text-destructive">The workbook cannot be read - nothing will be imported.</p>
            <ul className="mt-1 list-disc pl-5 text-sm">
              {report.fileErrors.map((e) => (
                <li key={e}>{e}</li>
              ))}
            </ul>
          </div>
        </Card>
      )}

      {report && report.fileErrors.length === 0 && (
        <>
          <MigrationSummary report={report} />

          <div className="flex flex-wrap items-center gap-2">
            {canImport && (
              <ConfirmButton
                onConfirm={onImport}
                disabled={busy}
                confirmText={`Click again to import ${report.summary.clientsImport} client(s)`}
              >
                <ShieldCheck className="mr-1.5 h-4 w-4" />
                {`Import ${report.summary.clientsImport} valid client(s)`}
              </ConfirmButton>
            )}
            <Button variant="outline" onClick={onDownload} disabled={busy}>
              <Download className="mr-1.5 h-4 w-4" />
              Download processed workbook
            </Button>
            {report.mode === 'commit' && (
              <span className="text-sm text-muted-foreground">
                Import finished. New RMs are waiting under Approvals; imported renewals start at
                Pending Verification.
              </span>
            )}
          </div>

          <Card className="p-0">
            <Tabs defaultValue="skipped">
              <TabsList className="m-3">
                <TabsTrigger value="skipped">{`Skipped (${skipped.length})`}</TabsTrigger>
                <TabsTrigger value="ready">
                  {`${report.mode === 'commit' ? 'Imported' : 'Ready'} (${ready.length})`}
                </TabsTrigger>
                <TabsTrigger value="existing">{`Already in CRM (${existing.length})`}</TabsTrigger>
                <TabsTrigger value="rms">{`RMs (${report.rms.length})`}</TabsTrigger>
                {report.unlinkedIssues.length > 0 && (
                  <TabsTrigger value="unlinked">{`Unlinked rows (${report.unlinkedIssues.length})`}</TabsTrigger>
                )}
              </TabsList>
              <TabsContent value="skipped">
                <MigrationClientTable clients={skipped} mode={report.mode} emptyText="No client was skipped." />
              </TabsContent>
              <TabsContent value="ready">
                <MigrationClientTable clients={ready} mode={report.mode} emptyText="No client passes every check yet." />
              </TabsContent>
              <TabsContent value="existing">
                <MigrationClientTable clients={existing} mode={report.mode} emptyText="None of these clients is in the CRM yet." />
              </TabsContent>
              <TabsContent value="rms">
                <MigrationRmTable rms={report.rms} mode={report.mode} />
              </TabsContent>
              <TabsContent value="unlinked">
                <div className="p-4">
                  <MigrationIssueList issues={report.unlinkedIssues} />
                </div>
              </TabsContent>
            </Tabs>
          </Card>
        </>
      )}
    </div>
  );
};

export default AdminMigrationPage;
